import { useEffect, useRef } from 'react'
import { useEntityRealtime } from '@/lib/realtime/core/useEntityRealtime'
import { useQueryClient } from '@tanstack/react-query'

import { SocketEvent } from '@scrambl/shared'

import type { useThreadReadSession } from './useThreadReadSession'

/** Socket/cross-tab bodies are hints, not authorized comment reader results. */
export function useThreadCommentReaderRealtime(
  threadId: string | undefined,
  queryKey: readonly unknown[],
  session: ReturnType<typeof useThreadReadSession>,
  enabled: boolean,
) {
  const client = useQueryClient()
  const target = JSON.stringify(queryKey)
  const current = useRef(target)
  current.current = target
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const refresh = () => {
    if (!enabled || !mounted.current || current.current !== target) return
    try {
      session.assertCurrent()
    } catch {
      return
    }
    void client.invalidateQueries({ queryKey, exact: true })
  }
  const { addEventListener, isConnected, socket } = useEntityRealtime(
    'thread',
    enabled ? threadId : null,
    {
      suppressLogging: true,
      onEntityUpdated: data => {
        if (data.id === threadId) refresh()
      },
      onEntityDeleted: data => {
        if (data.id === threadId) refresh()
      },
    },
  )
  const refreshRef = useRef(refresh)
  refreshRef.current = refresh
  useEffect(() => {
    if (!enabled || !threadId || !socket) return
    const capturedRefresh = refreshRef.current
    const remove = [
      SocketEvent.THREAD_COMMENT_CREATED,
      SocketEvent.THREAD_COMMENT_UPDATED,
      SocketEvent.THREAD_COMMENT_DELETED,
      SocketEvent.COMMENT_REACTION_ADDED,
      SocketEvent.COMMENT_REACTION_REMOVED,
    ].map(event =>
      addEventListener(
        event,
        data => {
          if (data.threadId === threadId) capturedRefresh()
        },
        { skipBroadcast: true },
      ),
    )
    return () => {
      remove.forEach(cleanup => cleanup())
    }
  }, [enabled, threadId, target, socket, addEventListener])
  const connection = useRef({ target, connectedOnce: false, connected: false })
  useEffect(() => {
    if (connection.current.target !== target)
      connection.current = { target, connectedOnce: false, connected: false }
    const previous = connection.current
    if (isConnected && previous.connectedOnce && !previous.connected)
      refreshRef.current()
    previous.connected = isConnected
    previous.connectedOnce ||= isConnected
  }, [isConnected, target])
}
